import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Activity, Link2, AlertTriangle, CheckCircle2, RefreshCw, Loader2, Copy, PlusCircle, X, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChannelLinkHealthCard() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  // Quick Link inline editor state — no need to hop to a separate page.
  const [linkingChannel, setLinkingChannel] = useState(null);
  const [linkIbId, setLinkIbId] = useState('');
  const [linkChannelType, setLinkChannelType] = useState('public');
  const [ibs, setIbs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Preload IB list once so the quick-link dropdown is populated the moment
    // admin clicks "Link to IB" on an unlinked channel.
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/ib/list`, { headers: { Authorization: `Bearer ${token}` } });
        setIbs(res.data?.ibs || res.data || []);
      } catch { /* silent */ }
    };
    load();
  }, []);

  const runCheck = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/telegram/channel-link-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Normalise response — backend returns different shape when MTProto is
      // disconnected (linked_configs only) vs connected (linked/unlinked/orphaned).
      const raw = res.data || {};
      const normalisedLinked = Array.isArray(raw.linked) && raw.linked.length > 0
        ? raw.linked
        : (raw.linked_configs || []).map(c => ({
            chat_id: c.chat_id,
            title: c.name,
            linked_name: c.name,
            linked_channel_type: c.channel_type,
            linked_ib_id: c.ib_id,
            linked_ib_name: c.ib_name,
          }));
      const normalised = {
        ...raw,
        linked: normalisedLinked,
        unlinked: raw.unlinked || [],
        orphaned: raw.orphaned || [],
      };
      if (raw.success) {
        setData(normalised);
      } else {
        setError(raw.error || 'Health check failed (MTProto may be offline in preview)');
        setData(normalised); // still show linked configs from DB
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Health check failed';
      setError(typeof msg === 'string' ? msg : 'Health check failed');
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    if (!data) return { linked: [], unlinked: [], orphaned: [] };
    const q = search.trim().toLowerCase();
    const bySearch = (arr) => q ? arr.filter(x =>
      ((x.title || x.name || '') + ' ' + (x.chat_id || '') + ' ' + (x.username || '')).toLowerCase().includes(q)
    ) : arr;
    return {
      linked: bySearch(data.linked || []),
      unlinked: bySearch(data.unlinked || []),
      orphaned: bySearch(data.orphaned || []),
    };
  }, [data, search]);

  const copyChatId = (chatId) => {
    navigator.clipboard.writeText(String(chatId));
    toast.success(`chat_id ${chatId} copied — paste in Add Integration form`);
  };

  const openQuickLink = (channel) => {
    setLinkingChannel(channel);
    setLinkIbId('');
    setLinkChannelType(channel?.type === 'private' ? 'private' : 'public');
  };

  const cancelQuickLink = () => {
    setLinkingChannel(null);
    setLinkIbId('');
  };

  const saveQuickLink = async () => {
    if (!linkingChannel || !linkIbId) {
      toast.error('Sila pilih IB dahulu');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/telegram/channels/config`, {
        ib_id: linkIbId,
        channel_type: linkChannelType,
        chat_id: String(linkingChannel.chat_id),
        username: linkingChannel.username || '',
        name: linkingChannel.title || linkingChannel.name || '',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Channel "${linkingChannel.title}" berjaya dilink ke IB!`);
      setLinkingChannel(null);
      setLinkIbId('');
      // refresh health check so linked channel moves out of unlinked list
      await runCheck();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Gagal simpan link';
      toast.error(typeof msg === 'string' ? msg : 'Gagal simpan link');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden" data-testid="channel-link-health-card">
      <div className="px-4 py-3 border-b border-[hsl(var(--border))] bg-gradient-to-r from-cyan-500/10 to-transparent flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-cyan-400" />
          <div>
            <h3 className="text-sm font-bold text-white">Channel Link Health Check</h3>
            <p className="text-[10px] text-[hsl(var(--text-muted))]">Detect unlinked channels + orphaned configs. Sekali klik → nampak status setiap channel.</p>
          </div>
        </div>
        <Button size="sm" onClick={runCheck} disabled={loading} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="channel-health-run-btn">
          {loading ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <RefreshCw size={12} className="mr-1.5" />}
          Run Check
        </Button>
      </div>

      {error && (
        <div className="px-4 py-2 bg-red-500/10 border-b border-red-500/20 text-[11px] text-red-300">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-2 p-3 border-b border-[hsl(var(--border))]" data-testid="channel-health-summary">
            <button onClick={() => setFilter('linked')} className={`p-2 rounded-lg border transition ${filter === 'linked' ? 'bg-emerald-500/15 border-emerald-500/40' : 'bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]'}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-[hsl(var(--text-muted))]">Linked</span>
                <CheckCircle2 size={12} className="text-emerald-400" />
              </div>
              <p className="text-xl font-bold text-emerald-400">{(data.linked || []).length || (data.linked_count ?? 0)}</p>
            </button>
            <button onClick={() => setFilter('unlinked')} className={`p-2 rounded-lg border transition ${filter === 'unlinked' ? 'bg-amber-500/15 border-amber-500/40' : 'bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]'}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-[hsl(var(--text-muted))]">Unlinked</span>
                <AlertTriangle size={12} className="text-amber-400" />
              </div>
              <p className="text-xl font-bold text-amber-400">{(data.unlinked || []).length}</p>
            </button>
            <button onClick={() => setFilter('orphaned')} className={`p-2 rounded-lg border transition ${filter === 'orphaned' ? 'bg-red-500/15 border-red-500/40' : 'bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]'}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-[hsl(var(--text-muted))]">Orphaned</span>
                <AlertTriangle size={12} className="text-red-400" />
              </div>
              <p className="text-xl font-bold text-red-400">{(data.orphaned || []).length}</p>
            </button>
          </div>

          {/* Search */}
          <div className="px-3 py-2 border-b border-[hsl(var(--border))]">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title / chat_id / username..."
              className="h-8 w-full text-xs bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded px-2 text-white"
              data-testid="channel-health-search"
            />
          </div>

          {/* Filtered lists */}
          <div className="max-h-[520px] overflow-y-auto">
            {/* UNLINKED — highest priority for admin action */}
            {(filter === 'all' || filter === 'unlinked') && filtered.unlinked.length > 0 && (
              <div className="p-3 border-b border-[hsl(var(--border))]">
                <p className="text-[10px] uppercase tracking-widest font-bold text-amber-400 mb-2">⚠ Unlinked ({filtered.unlinked.length}) — Boleh access via MTProto tapi belum di-link</p>
                <div className="space-y-2">
                  {filtered.unlinked.map((c) => (
                    <div key={c.chat_id} className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-2" data-testid={`health-unlinked-${c.chat_id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Link2 size={11} className="text-amber-400" />
                          <span className="text-sm font-medium text-white">{c.title}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-300">{c.type}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => openQuickLink(c)}
                            className="text-[10px] px-2 py-1 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 flex items-center gap-1 border border-emerald-500/30"
                            data-testid={`quick-link-btn-${c.chat_id}`}
                          >
                            <PlusCircle size={9} /> Link to IB
                          </button>
                          <button onClick={() => copyChatId(c.chat_id)} className="text-[10px] px-2 py-1 rounded bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25 flex items-center gap-1" data-testid={`copy-chat-id-${c.chat_id}`}>
                            <Copy size={9} /> Copy chat_id
                          </button>
                        </div>
                      </div>
                      <div className="text-[10px] font-mono text-[hsl(var(--text-muted))]">
                        chat_id: <span className="text-cyan-300">{c.chat_id}</span>
                        {c.username && <span className="ml-2">· @{c.username}</span>}
                        {c.members_count != null && <span className="ml-2">· {c.members_count} members</span>}
                      </div>
                      {c.last_message_excerpt && (
                        <pre className="mt-1.5 text-[10px] text-[hsl(var(--text))] bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded p-1.5 whitespace-pre-wrap break-words font-mono max-h-20 overflow-y-auto">
{c.last_message_excerpt}
                        </pre>
                      )}
                      {linkingChannel?.chat_id === c.chat_id && (
                        <div className="mt-2 p-2 rounded border border-emerald-500/30 bg-emerald-500/10 space-y-2" data-testid={`quick-link-form-${c.chat_id}`}>
                          <div className="flex items-center justify-between">
                            <p className="text-[10px] font-bold text-emerald-300 flex items-center gap-1">
                              <Info size={10} /> Link channel ini ke IB mana?
                            </p>
                            <button onClick={cancelQuickLink} className="text-[hsl(var(--text-muted))] hover:text-white" data-testid={`quick-link-cancel-${c.chat_id}`}>
                              <X size={12} />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Select value={linkIbId} onValueChange={setLinkIbId}>
                              <SelectTrigger className="h-8 text-xs bg-[hsl(var(--surface-elevated))]" data-testid={`quick-link-ib-select-${c.chat_id}`}>
                                <SelectValue placeholder="Pilih IB..." />
                              </SelectTrigger>
                              <SelectContent>
                                {ibs.map((ib) => (
                                  <SelectItem key={ib.id} value={ib.id} data-testid={`quick-link-ib-option-${ib.id}`}>
                                    {ib.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Select value={linkChannelType} onValueChange={setLinkChannelType}>
                              <SelectTrigger className="h-8 text-xs bg-[hsl(var(--surface-elevated))]" data-testid={`quick-link-type-select-${c.chat_id}`}>
                                <SelectValue placeholder="Type..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="public">Public</SelectItem>
                                <SelectItem value="private">Private</SelectItem>
                                <SelectItem value="signal">Signal</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            size="sm"
                            onClick={saveQuickLink}
                            disabled={saving || !linkIbId}
                            className="w-full h-7 text-[11px] bg-emerald-500 hover:bg-emerald-600 text-white"
                            data-testid={`quick-link-save-${c.chat_id}`}
                          >
                            {saving ? <Loader2 size={11} className="mr-1 animate-spin" /> : <CheckCircle2 size={11} className="mr-1" />}
                            Simpan Link
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ORPHANED — config exists but MTProto lost access */}
            {(filter === 'all' || filter === 'orphaned') && filtered.orphaned.length > 0 && (
              <div className="p-3 border-b border-[hsl(var(--border))]">
                <p className="text-[10px] uppercase tracking-widest font-bold text-red-400 mb-2">✗ Orphaned ({filtered.orphaned.length}) — Config wujud tapi MTProto tak boleh access</p>
                <div className="space-y-2">
                  {filtered.orphaned.map((c) => (
                    <div key={c.chat_id} className="rounded-lg border border-red-500/20 bg-red-500/5 p-2" data-testid={`health-orphaned-${c.chat_id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-white">{c.name || c.chat_id}</p>
                          <div className="text-[10px] font-mono text-[hsl(var(--text-muted))]">
                            chat_id: <span className="text-red-300">{c.chat_id}</span>
                            {c.channel_type && <span className="ml-2 text-slate-300">· {c.channel_type}</span>}
                            {c.ib_name && <span className="ml-2">→ {c.ib_name}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* LINKED — healthy */}
            {(filter === 'all' || filter === 'linked') && filtered.linked.length > 0 && (
              <div className="p-3">
                <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-400 mb-2">✓ Linked &amp; Healthy ({filtered.linked.length})</p>
                <div className="space-y-1.5">
                  {filtered.linked.map((c) => (
                    <div key={c.chat_id} className="rounded-lg border border-emerald-500/15 bg-emerald-500/5 p-2" data-testid={`health-linked-${c.chat_id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-white">
                            {c.title || c.linked_name}
                            {c.linked_channel_type && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-300 uppercase">{c.linked_channel_type}</span>}
                          </p>
                          <div className="text-[10px] font-mono text-[hsl(var(--text-muted))]">
                            chat_id: <span className="text-emerald-300">{c.chat_id}</span>
                            {c.linked_ib_name && <span className="ml-2">→ {c.linked_ib_name}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty */}
            {filter !== 'all' && filtered[filter].length === 0 && (
              <div className="p-6 text-center text-[11px] text-[hsl(var(--text-muted))]">
                Tiada channel dalam kategori &quot;{filter}&quot;.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
